import axios from 'axios';
import type { ReportData } from '../reports_doc/MathReport';
import { apiErrorMessage } from '../../reports/edit/editApi';

/** Базовый адрес мат. отчета (все запросы проверяются сервером по сессии и правам роли) */
export const MATH_API = '/api/v2/mathreport';

/** Колонка таблицы: набор и подписи настраиваются на вкладке «Отображение» */
export interface MathColumn {
  field: string;
  headerName: string;
  editable: boolean;
}

/** Запись мат. отчета аптеки за месяц. Суммы приходят числом, строкой (decimal) или null */
export interface MathRow {
  id: number;
  pharmacyNum: string;
  [field: string]: unknown;
}

export interface MathPeriod {
  month: number;
  year: number;
}

interface MathColumnDto {
  field: string;
  headerName: string;
  editable: number | boolean;
}

interface ActionResponse {
  success?: boolean;
  message?: string;
  updatedMonths?: number;
}

/** Сервер мат. отчета отдает текст ошибки и отказа по правам в поле `message` (как и вся отчетность) */
export function mathErrorMessage(error: unknown, fallback: string): string {
  return apiErrorMessage(error, fallback);
}

function ensureSuccess(data: ActionResponse | undefined, fallback: string): ActionResponse {
  if (!data || !data.success) throw new Error(fallback);
  return data;
}

export async function fetchMathColumns(): Promise<MathColumn[]> {
  const res = await axios.get<MathColumnDto[]>(`${MATH_API}/fields`);
  const list = Array.isArray(res.data) ? res.data : [];
  return list.map((item) => ({ field: item.field, headerName: item.headerName, editable: Boolean(item.editable) }));
}

/** Весь месяц целиком, без постраничной загрузки: страницы режутся на экране */
export async function fetchMathRows(period: MathPeriod): Promise<MathRow[]> {
  const res = await axios.get<MathRow[]>(`${MATH_API}/data`, { params: period });
  return Array.isArray(res.data) ? res.data : [];
}

export async function saveMathCell(id: number, field: string, value: number): Promise<void> {
  await axios.put(`${MATH_API}/data/${id}/cell`, { field, value });
}

export async function saveMathStatus(id: number, value: 0 | 1): Promise<void> {
  await axios.put(`${MATH_API}/data/${id}/status`, { value });
}

/** Создает записи месяца для всех аптек области видимости; возвращает текст результата от сервера */
export async function createMathRecords(period: MathPeriod): Promise<string> {
  const res = await axios.post<ActionResponse>(`${MATH_API}/data/create`, period);
  return ensureSuccess(res.data, 'Не удалось создать записи').message || 'Записи созданы';
}

/** Добавляет недостающие записи месяца; возвращает текст результата от сервера */
export async function reindexMathRecords(period: MathPeriod): Promise<string> {
  const res = await axios.post<ActionResponse>(`${MATH_API}/data/reindex`, period);
  return ensureSuccess(res.data, 'Не удалось выполнить перепроверку').message || 'Перепроверка выполнена';
}

/** Пересчитывает остатки с выбранного месяца; возвращает число обновленных месяцев */
export async function recalculateMath(period: MathPeriod): Promise<number> {
  const res = await axios.post<ActionResponse>(`${MATH_API}/data/recalculate`, period);
  return Number(ensureSuccess(res.data, 'Не удалось выполнить пересчет').updatedMonths) || 0;
}

/**
 * Формирует Word-документ «Материальный отчет» по строке. Библиотеки документа тяжелые,
 * поэтому подгружаются только при первом скачивании.
 */
export async function downloadMathDocument(row: MathRow, month: string): Promise<void> {
  const res = await axios.get<{ pharmacyNum: string; fullName: string }>(`${MATH_API}/pharmacy-info`, {
    params: { login: row.pharmacyNum, rowId: row.id }
  });
  if (!res.data) throw new Error('Пользователь не найден');

  const { generateMathReport } = await import('../reports_doc/MathReport');
  await generateMathReport(row as ReportData, month, res.data.pharmacyNum, res.data.fullName);
}
